import type { WarningNotice } from '@/pages/accounting/statements/statementMeta';
import { collectAbnormalBalances } from './utils';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { Selection } from '@heroui/react';
import {
    useChartOfAccounts,
    useSetAccountArchivedMutation,
} from '@/api/queries';
import type { ChartAccount, LocaleCode } from '@/api/models';
import type { ErrorResponse } from '@/api/handlers/responses';
import { getAuthMethod, getUser } from '@/utils/authSession';
import { PATH } from '@/router/PATH';
import { useBooksRange } from '../booksOutletContext';
import {
    canReadAccounts,
    canWriteAccounts,
    deriveAccountsAccess,
    type AccountsAccess,
} from './accountsAccess';
import { buildAccountTree, type BuildAccountTreeResult } from './utils';

const SUPPORTED_LOCALES: LocaleCode[] = ['ka', 'en'];

const resolveLocale = (language: string): LocaleCode =>
    (SUPPORTED_LOCALES as string[]).includes(language)
        ? (language as LocaleCode)
        : 'en';

/** Create/edit drawer state, owned by the page. */
export type AccountDrawerState = {
    isOpen: boolean;
    /** The row being edited; `null` = create. The drawer re-reads it by id. */
    account: ChartAccount | null;
    /** Bumped per open so each open mounts a fresh form. */
    instanceId: number;
};

export type UseChartOfAccountsPageResult = {
    tree: BuildAccountTreeResult;
    /**
     * Advisory for the abnormal balances on screen — empty when there are none
     * (BOS-163). Derived from the rendered rows, not from a second request.
     */
    abnormalNotices: WarningNotice[];
    /** Dismiss the abnormal-balance advisory for this visit. */
    onDismissAbnormal: () => void;
    data: ChartAccount[] | null;
    isLoading: boolean;
    error: ErrorResponse | null;
    access: AccountsAccess;
    /** May the user add / edit / archive? */
    canWrite: boolean;
    /**
     * A fresh tenant with no chart at all. The page routes to onboarding rather
     * than drawing an empty grid — the two look identical and mean opposite
     * things.
     */
    bootstrapRequired: boolean;
    locale: LocaleCode;
    search: string;
    showArchived: boolean;
    /** Effective expansion (user expansion ∪ search auto-expansion). */
    expandedKeys: Set<string>;
    onSearchChange: (value: string) => void;
    onToggleArchived: (next: boolean) => void;
    onExpandedChange: (keys: Selection) => void;
    onSelectAccount: (account: ChartAccount) => void;
    onRetry: () => void;
    // Create / edit / archive
    drawer: AccountDrawerState;
    onAddAccount: () => void;
    onEditAccount: (account: ChartAccount) => void;
    onReactivateAccount: (account: ChartAccount) => void;
    onCloseDrawer: () => void;
    onSavedAccount: () => void;
};

type UseChartOfAccountsPageOptions = {
    /**
     * Row-activation handler. Defaults to routing to the Journal scoped to the
     * account; kept injectable so the target is trivial to retarget.
     */
    onSelectAccount?: (account: ChartAccount) => void;
};

/**
 * Drives the Chart of Accounts page: derives access, fetches the chart, builds
 * the tree in a memo (pure util), and owns the toolbar / expansion state.
 *
 * Access is derived **before** the fetch, not discovered from a 403. A PIN
 * session is refused by every accounts endpoint regardless of role, so the page
 * doesn't ask — it explains. See `accountsAccess`.
 *
 * Expansion model: `userExpandedKeys` is what the user has toggled and defaults
 * to the top-level classes only, so the chart opens as classes + groups with
 * individual accounts collapsed. The standard Georgian chart is ~260 rows —
 * expanding everything on load buries the structure the codes exist to express.
 * While searching, the effective set additionally includes the matched
 * branches' ancestors so hits show in context — clearing the search drops back
 * to the user's set.
 */
export const useChartOfAccountsPage = ({
    onSelectAccount,
}: UseChartOfAccountsPageOptions = {}): UseChartOfAccountsPageResult => {
    const { i18n } = useTranslation();
    const navigate = useNavigate();
    const locale = resolveLocale(i18n.language);

    // Read once per render from the persisted session; both are synchronous
    // localStorage/JWT reads, not state.
    const access = React.useMemo(
        () => deriveAccountsAccess(getUser(), getAuthMethod()),
        [],
    );
    const canRead = canReadAccounts(access);
    const canWrite = canWriteAccounts(access);

    // The Books shell owns the date range; this tab reads it rather than the
    // query string, so the chart and the picker above it can't disagree.
    //
    // Only the range **end** reaches the feed. An account balance is cumulative
    // — "cash at 31 March" is an answer, "cash between January and March" is
    // not — so the accounts endpoint takes a single `asOf` and the start date
    // has nothing to say to it. An empty range (this page rendered outside the
    // shell) sends no `asOf` at all, which is the live-balance feed.
    const { fromDate, toDate } = useBooksRange();

    const { data, bootstrapRequired, isLoading, error, refetch } =
        useChartOfAccounts({
            locale,
            enabled: canRead,
            asOf: toDate || undefined,
        });

    const [search, setSearch] = React.useState('');
    const [showArchived, setShowArchived] = React.useState(false);
    const [userExpandedKeys, setUserExpandedKeys] = React.useState<Set<string>>(
        () => new Set(),
    );
    const hasInitializedExpansion = React.useRef(false);

    const tree = React.useMemo(
        () =>
            buildAccountTree(data ?? [], {
                search,
                showArchived,
                locale,
            }),
        [data, search, showArchived, locale],
    );

    // Open with the classes expanded and their accounts collapsed — classes and
    // groups visible (1000 → 1100), individual accounts (1110) one click away.
    // Only runs once; later user toggles own the state.
    React.useEffect(() => {
        if (
            !hasInitializedExpansion.current &&
            tree.defaultExpandedIds.length > 0
        ) {
            setUserExpandedKeys(new Set(tree.defaultExpandedIds));
            hasInitializedExpansion.current = true;
        }
    }, [tree.defaultExpandedIds]);

    // Effective expansion = user's set ∪ search auto-expansion.
    const expandedKeys = React.useMemo(() => {
        if (!tree.isSearching) {
            return userExpandedKeys;
        }
        return new Set<string>([...userExpandedKeys, ...tree.autoExpandIds]);
    }, [tree.isSearching, tree.autoExpandIds, userExpandedKeys]);

    const onExpandedChange = React.useCallback(
        (keys: Selection) => {
            setUserExpandedKeys(
                keys === 'all'
                    ? new Set(tree.allGroupIds)
                    : new Set(Array.from(keys, String)),
            );
        },
        [tree.allGroupIds],
    );

    const onSearchChange = React.useCallback((value: string) => {
        setSearch(value);
    }, []);

    const onToggleArchived = React.useCallback((next: boolean) => {
        setShowArchived(next);
    }, []);

    // Row activation stays inside the experiment: it lands on the **Journal
    // tab**, not on `/accounting/journal`, and carries the shell's window so the
    // picker above reads the same before and after the jump.
    const defaultSelectAccount = React.useCallback(
        (account: ChartAccount) => {
            navigate(
                PATH.ACCOUNTING.BOOKS.BIND_JOURNAL_FOR_ACCOUNT(
                    account.id,
                    fromDate && toDate
                        ? { from: fromDate, to: toDate }
                        : undefined,
                ),
            );
        },
        [navigate, fromDate, toDate],
    );

    const onRetry = React.useCallback(() => {
        refetch();
    }, [refetch]);

    // --- Create / edit / archive -------------------------------------------
    const [drawer, setDrawer] = React.useState<AccountDrawerState>({
        isOpen: false,
        account: null,
        instanceId: 0,
    });
    // Destructure the stable `mutate` (the mutation result object is a fresh
    // reference every render; depending on it would re-create the row-action
    // callbacks and re-render the whole DataGrid on each keystroke).
    const { mutate: setAccountArchived } = useSetAccountArchivedMutation();

    const onAddAccount = React.useCallback(() => {
        setDrawer((prev) => ({
            isOpen: true,
            account: null,
            instanceId: prev.instanceId + 1,
        }));
    }, []);

    const onEditAccount = React.useCallback((account: ChartAccount) => {
        setDrawer((prev) => ({
            isOpen: true,
            account,
            instanceId: prev.instanceId + 1,
        }));
    }, []);

    const onCloseDrawer = React.useCallback(() => {
        setDrawer((prev) => ({ ...prev, isOpen: false }));
    }, []);

    // Save/create/archive invalidate the chart query from inside the form; the
    // page just closes the drawer so the refreshed tree shows through.
    const onSavedAccount = React.useCallback(() => {
        setDrawer((prev) => ({ ...prev, isOpen: false }));
    }, []);

    // Row quick-action: reactivation has no precondition, so fire it directly.
    const onReactivateAccount = React.useCallback(
        (account: ChartAccount) => {
            setAccountArchived({ id: account.id, archived: false });
        },
        [setAccountArchived],
    );

    // BOS-163: the chart painted an abnormal balance red and said nothing
    // else, while the advisory that explains red lived on the Statements tab.
    // Derived from the rows already on screen, so it cannot disagree with them
    // and costs no second request.
    const [abnormalDismissed, setAbnormalDismissed] = React.useState(false);
    const onDismissAbnormal = React.useCallback(
        () => setAbnormalDismissed(true),
        [],
    );

    const abnormalNotices = React.useMemo<WarningNotice[]>(() => {
        const abnormal = abnormalDismissed
            ? []
            : collectAbnormalBalances(tree.roots);
        return abnormal.length === 0
            ? []
            : [
                  {
                      kind: 'localized' as const,
                      // `pages`-namespace-relative, like every other key
                      // `StatementWarnings` resolves — it calls
                      // `useTranslation('pages')`. The `accounting.` prefix was
                      // a path that does not exist, so a reader with a red
                      // balance saw the raw key (BOS-171).
                      key: 'chartOfAccounts.abnormalBalances',
                      params: {
                          count: String(abnormal.length),
                          codes: abnormal
                              .map((entry) => entry.accountCode)
                              .join(', '),
                      },
                  },
              ];
    }, [abnormalDismissed, tree.roots]);

    return {
        tree,
        abnormalNotices,
        onDismissAbnormal,
        data,
        // A session that will never be allowed to read is not "loading".
        isLoading: canRead && isLoading,
        error,
        access,
        canWrite,
        bootstrapRequired,
        locale,
        search,
        showArchived,
        expandedKeys,
        onSearchChange,
        onToggleArchived,
        onExpandedChange,
        onSelectAccount: onSelectAccount ?? defaultSelectAccount,
        onRetry,
        drawer,
        onAddAccount,
        onEditAccount,
        onReactivateAccount,
        onCloseDrawer,
        onSavedAccount,
    };
};
